import { appendSearchParams } from '@alexis/helpers/searchParam';
import { sessionStorageGet, sessionStorageSave } from '@alexis/helpers/sessionStorage';
import axios from 'axios';

export interface AncillaryService {
  named_key: string;
  homepage_icon: string;
  homepage_title: string;
  deeplink: string;
}

export interface AncillaryServicesData {
  features: AncillaryService[];
}

export interface AncillaryServices {
  success: boolean;
  message: string;
  data: AncillaryServicesData;
}

interface CachedAncillaryServices {
  data: AncillaryService[];
  timestamp: number;
}

// Cache duration in milliseconds (30 minutes)
const CACHE_DURATION = 30 * 60 * 1000;

export async function getAncillaryServices(
  apiBaseUrl: string,
  currentSiteCode: string,
  locale: string,
): Promise<AncillaryService[]> {
  const cacheKey = `ancillary_services_${currentSiteCode}_desktop_${locale}`;

  // Check if we have cached data
  const cachedData = sessionStorageGet<CachedAncillaryServices>(cacheKey);

  if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
    // Return cached data if it's still valid
    return cachedData.data;
  }

  const searchParams = {
    siteCode: currentSiteCode.toUpperCase(),
    language: locale,
    appType: 'WEB_APP',
  };

  const url = appendSearchParams(`${apiBaseUrl}/server-driven-configs/v1/features`, searchParams);

  const response = await axios.get<AncillaryServices>(url, { withCredentials: true });

  if (response.status === 200) {
    // Cache the response data
    const dataToCache: CachedAncillaryServices = {
      data: response.data.data?.features ?? [],
      timestamp: Date.now(),
    };
    sessionStorageSave(cacheKey, dataToCache);

    return response.data.data?.features ?? [];
  }
  throw new Error(response.statusText);
}
